import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, AlertCircle, Heart } from 'lucide-react';
import Disclaimer from '../../components/Disclaimer';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const SecuriteProtectionJoueurs = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Disclaimer />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/guides" data-discover="true" className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux guides
        </Link>

        <article className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Sécurité et Protection des Joueurs : Vos Droits et Outils
          </h1>
          
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6 leading-relaxed">
            <p className="text-xl text-gray-600 font-medium">
              Parier en ligne en toute sécurité ne se résume pas à choisir un site agréé. Il faut comprendre vos droits, connaître les 
              outils de protection disponibles, et savoir comment réagir en cas de problème. Ce guide complet vous explique tout ce 
              que vous devez savoir pour protéger votre sécurité, vos données, et votre bien-être en tant que parieur.
            </p>

            <div className="bg-purple-50 border-l-4 border-purple-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-6 h-6 text-purple-600" />
                L'Agrément ANJ : Votre Première Protection
              </h2>
              <p>
                L'agrément de l'Autorité Nationale des Jeux (ANJ) n'est pas seulement une garantie de légalité : c'est votre assurance 
                que le site respecte des normes strictes de sécurité, de protection des données, et de jeu responsable. Les sites agréés 
                sont régulièrement audités, doivent respecter des règles strictes sur les bonus et les retraits, et sont tenus de fournir 
                des outils de protection des joueurs.
              </p>
              <p>
                Si vous avez un problème avec un site agréé, vous pouvez porter plainte auprès de l'ANJ, qui a le pouvoir d'enquêter 
                et de sanctionner. Les sites non agréés n'offrent aucune protection : en cas de problème, vous n'avez aucun recours. 
                Vérifiez toujours l'agrément avant de créer un compte, et ne faites jamais confiance à un site qui ne peut pas prouver 
                son agrément.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              La Protection de Vos Données Personnelles
            </h2>
            <p>
              Les sites agréés sont soumis au RGPD (Règlement Général sur la Protection des Données) et doivent protéger vos informations 
              personnelles. Vos données bancaires sont cryptées lors des transactions, et les sites ne peuvent pas partager vos informations 
              sans votre consentement explicite. Vérifiez toujours la politique de confidentialité avant de vous inscrire.
            </p>
            <p>
              Méfiez-vous des sites qui demandent des informations excessives ou qui partagent vos données avec des tiers sans raison 
              claire. Un site sérieux demande uniquement les informations nécessaires pour l'inscription, la vérification d'identité, 
              et les transactions. Si un site semble trop intrusif dans ses demandes d'informations, c'est un signal d'alarme.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Lock className="w-6 h-6 text-blue-600" />
                Les Outils de Jeu Responsable
              </h2>
              <p>
                Tous les sites agréés doivent offrir des outils de jeu responsable. Les limites de dépôt vous permettent de fixer un 
                montant maximum que vous pouvez déposer par jour, semaine, ou mois. Les limites de mise limitent le montant que vous 
                pouvez miser sur une période donnée. Les limites de temps vous permettent de limiter votre temps de jeu quotidien ou hebdomadaire.
              </p>
              <p>
                L'auto-exclusion temporaire vous permet de suspendre votre compte pour une période déterminée (24 heures, 7 jours, 30 jours, 
                etc.). L'auto-exclusion définitive ferme votre compte de manière permanente. Ces outils sont gratuits, faciles à utiliser, 
                et doivent être respectés immédiatement par le site. Utilisez-les si vous sentez que vous perdez le contrôle de vos paris.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Reconnaître les Signes de Problème
            </h2>
            <p>
              Le jeu responsable commence par la connaissance de soi. Les signes d'alerte incluent : parier plus que vous ne pouvez vous 
              permettre de perdre, essayer de "rattraper" des pertes en augmentant vos mises, mentir sur vos habitudes de jeu, négliger 
              vos responsabilités à cause des paris, ou ressentir de l'anxiété ou de la culpabilité liées aux paris.
            </p>
            <p>
              Si vous reconnaissez ces signes, utilisez immédiatement les outils de jeu responsable disponibles sur le site. Vous pouvez 
              aussi contacter des associations d'aide comme SOS Joueurs ou Joueurs Info Service, qui offrent un soutien gratuit et 
              confidentiel. Le jeu doit rester un loisir : s'il devient une source de stress ou de problèmes, il est temps de faire une pause.
            </p>

            <div className="bg-green-50 border-l-4 border-green-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-green-600" />
                Protéger Vos Comptes et Transactions
              </h2>
              <p>
                La sécurité de votre compte commence par un mot de passe fort et unique. Utilisez une combinaison de lettres, chiffres, 
                et caractères spéciaux, et ne réutilisez jamais le même mot de passe sur plusieurs sites. Activez l'authentification à 
                deux facteurs si disponible : elle ajoute une couche de sécurité supplémentaire en exigeant un code supplémentaire pour 
                vous connecter.
              </p>
              <p>
                Vérifiez régulièrement l'historique de vos transactions pour détecter toute activité suspecte. Les sites agréés doivent 
                vous permettre de consulter toutes vos transactions en détail. Si vous remarquez une transaction que vous n'avez pas 
                effectuée, contactez immédiatement le service client et changez votre mot de passe. Les sites sérieux ont des procédures 
                claires pour gérer ces situations.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Comprendre Vos Droits en Cas de Litige
            </h2>
            <p>
              Si vous avez un problème avec un site agréé (retrait refusé, bonus non crédité, compte fermé sans raison, etc.), vous avez 
              plusieurs recours. Commencez par contacter le service client du site : la plupart des problèmes peuvent être résolus rapidement 
              par communication directe. Gardez toujours une trace écrite de vos échanges (emails, captures d'écran).
            </p>
            <p>
              Si le problème persiste, vous pouvez porter plainte auprès de l'ANJ, qui a un service de médiation pour les litiges entre 
              joueurs et opérateurs. L'ANJ peut enquêter, demander des explications au site, et même sanctionner si nécessaire. Les sites 
              agréés sont tenus de coopérer avec l'ANJ dans le cadre des médiations. C'est une protection importante que vous n'avez pas 
              avec les sites non agréés.
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Heart className="w-6 h-6 text-yellow-600" />
                Les Ressources d'Aide et de Soutien
              </h2>
              <p>
                Plusieurs organisations offrent de l'aide aux joueurs en difficulté. SOS Joueurs (sosjoueurs.org) offre un soutien 
                gratuit et confidentiel par téléphone, email, ou chat. Joueurs Info Service (joueurs-info-service.fr) fournit des 
                informations et de l'aide pour le jeu responsable. Evalujeu (evalujeu.fr) propose un test d'auto-évaluation pour identifier 
                les risques.
              </p>
              <p>
                Ces ressources sont gratuites, confidentielles, et disponibles 24/7. N'hésitez pas à les contacter si vous avez des questions 
                ou des préoccupations. Le jeu responsable n'est pas une faiblesse : c'est une approche mature et saine des paris en ligne. 
                Utiliser ces ressources montre que vous prenez votre bien-être au sérieux.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              La Transparence des Conditions et Bonus
            </h2>
            <p>
              Les sites agréés sont tenus d'afficher clairement toutes les conditions importantes : conditions générales, conditions des bonus, 
              délais de retrait, frais éventuels, etc. Si un site cache ces informations ou les rend difficiles à trouver, c'est un 
              problème. Lisez toujours ces conditions avant d'accepter un bonus ou de faire un dépôt important.
            </p>
            <p>
              Les bonus doivent avoir des conditions claires et raisonnables. Si les conditions semblent trop complexes ou trop restrictives, 
              méfiez-vous. Un bonus avec des conditions impossibles à remplir n'est pas vraiment un bonus : c'est un piège. Les sites sérieux 
              affichent leurs conditions de manière transparente et les expliquent clairement.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Notre Approche Recommandée
            </h2>
            <p>
              Pour parier en toute sécurité, choisissez uniquement des sites agréés par l'ANJ, utilisez des mots de passe forts et uniques, 
              activez l'authentification à deux facteurs si disponible, fixez des limites de dépôt et de mise dès le départ, surveillez 
              régulièrement vos transactions, lisez toujours les conditions avant d'accepter des offres, et utilisez les outils de jeu 
              responsable si nécessaire.
            </p>
            <p>
              La sécurité et la protection des joueurs ne sont pas des options : ce sont des droits fondamentaux. Les sites agréés en France 
              sont tenus de respecter des normes strictes, mais vous devez aussi prendre vos responsabilités en tant que joueur. Utilisez 
              les outils disponibles, restez informé de vos droits, et n'hésitez pas à demander de l'aide si vous en avez besoin. Le jeu 
              doit rester un loisir agréable et contrôlé : si ce n'est plus le cas, utilisez les ressources d'aide disponibles. Votre 
              sécurité et votre bien-être sont plus importants que n'importe quel gain potentiel.
            </p>
          </div>
        </article>
      </div>

      <Footer />
    </div>
  );
};

export default SecuriteProtectionJoueurs;

